// Knowledge-base tab of /chatbot/settings: URLs and uploaded documents the
// concierge may quote.

import { useCallback, useEffect, useRef, useState } from "react";

import { http } from "../api";
import { Badge, Confirm, Empty, SkeletonRows, Spinner, Toggle, useToast } from "../ui";
import { ErrorBanner, errMessage, fmtDate } from "./shared";

interface WikiSource {
  id: string;
  url?: string;
  title?: string;
  file_name?: string;
  source_type?: string;
  is_enabled: boolean;
  content?: string;
  summary?: string;
  fetch_error?: string;
  last_fetched_at?: string;
  tags?: string[];
}

/** Knowledge-base tab: URLs and uploaded documents the concierge may quote. */
export function WikiSourcesPanel({ canWrite }: { canWrite: boolean }) {
  const toast = useToast();
  const [sources, setSources] = useState<WikiSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<"url" | "file">("url");
  const [url, setUrl] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [toDelete, setToDelete] = useState<WikiSource | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await http.get<WikiSource[]>("/chatbot/wiki-sources");
      setSources(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      setError(err);
      setSources([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const add = async () => {
    setBusy(true);
    try {
      if (mode === "file") {
        if (!file) return;
        const form = new FormData();
        form.append("file", file);
        await http.upload("/chatbot/wiki-sources/upload", form);
        setFile(null);
        if (fileRef.current) fileRef.current.value = "";
        toast.push("File uploaded. Extraction in progress…", "good");
      } else {
        if (!url.trim()) return;
        await http.post("/chatbot/wiki-sources", { url: url.trim() });
        setUrl("");
        toast.push("Source added. Extraction in progress…", "good");
      }
      // Extraction is asynchronous: show the row now, refresh once it has had
      // a moment to finish.
      void load();
      window.setTimeout(() => void load(), 3000);
    } catch (err) {
      toast.push(errMessage(err, "Failed to add the source"), "critical");
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (s: WikiSource, enabled: boolean) => {
    try {
      await http.put(`/chatbot/wiki-sources/${s.id}`, { is_enabled: enabled });
      setSources((list) => list.map((x) => (x.id === s.id ? { ...x, is_enabled: enabled } : x)));
    } catch (err) {
      toast.push(errMessage(err, "Failed to update the source"), "critical");
    }
  };

  const refresh = async (id: string) => {
    try {
      await http.post(`/chatbot/wiki-sources/${id}/refresh`);
      toast.push("Refresh started…", "good");
      window.setTimeout(() => void load(), 5000);
    } catch (err) {
      toast.push(errMessage(err, "Failed to refresh"), "critical");
    }
  };

  const reExtract = async (id: string) => {
    try {
      await http.post(`/chatbot/wiki-sources/${id}/re-extract`);
      toast.push("Re-extraction started…", "good");
      window.setTimeout(() => void load(), 5000);
    } catch (err) {
      toast.push(errMessage(err, "Failed to re-extract"), "critical");
    }
  };

  const refreshAll = async () => {
    setBusy(true);
    try {
      await http.post("/chatbot/wiki-sources/refresh-all");
      toast.push("Refresh started for all sources…", "good");
      window.setTimeout(() => void load(), 8000);
    } catch (err) {
      toast.push(errMessage(err, "Failed to refresh all sources"), "critical");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!toDelete) return;
    try {
      await http.del(`/chatbot/wiki-sources/${toDelete.id}`);
      setSources((list) => list.filter((s) => s.id !== toDelete.id));
      toast.push("Source removed", "good");
    } catch (err) {
      toast.push(errMessage(err, "Failed to delete the source"), "critical");
    } finally {
      setToDelete(null);
    }
  };

  const viewFile = async (s: WikiSource) => {
    try {
      const res = await http.raw(`/chatbot/wiki-sources/${s.id}/file`);
      const blobUrl = URL.createObjectURL(await res.blob());
      window.open(blobUrl, "_blank", "noopener,noreferrer");
      // The new tab needs the URL to survive long enough to load.
      window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
    } catch (err) {
      toast.push(errMessage(err, "Failed to load the file"), "critical");
    }
  };

  const status = (s: WikiSource): { label: string; tone: "good" | "warning" | "critical" | "info" | "neutral" } => {
    if (s.fetch_error) {
      return s.fetch_error.includes("No relevant")
        ? { label: "No content", tone: "warning" }
        : { label: "Error", tone: "critical" };
    }
    if (s.content) return { label: "OK", tone: "good" };
    if (s.last_fetched_at) return { label: "Processing", tone: "info" };
    return { label: "Pending", tone: "neutral" };
  };

  return (
    <div className="card flush">
      <div className="card-head">
        <h3>Knowledge base sources</h3>
      </div>
      <div className="card-body">
        <p className="muted" style={{ fontSize: 12 }}>
          Add URLs or upload PDF/DOCX/TXT documents. Content is extracted automatically and used to ground replies.
        </p>

        <ErrorBanner error={error} what="Could not load the knowledge base sources" />

        <div className="toolbar">
          <div className="btn-group">
            <button className={`btn sm${mode === "url" ? " active" : ""}`} onClick={() => setMode("url")}>
              URL
            </button>
            <button className={`btn sm${mode === "file" ? " active" : ""}`} onClick={() => setMode("file")}>
              File
            </button>
          </div>
          {mode === "url" ? (
            <input
              className="grow"
              value={url}
              placeholder="https://cybercrime.gov.in/…"
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void add();
              }}
            />
          ) : (
            <input
              className="grow"
              ref={fileRef}
              type="file"
              accept=".pdf,.docx,.txt"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
          )}
          <button
            className="btn primary sm"
            disabled={!canWrite || busy || (mode === "url" ? !url.trim() : !file)}
            onClick={() => void add()}
          >
            {busy ? <Spinner /> : "+"} Add
          </button>
          {sources.length > 0 && (
            <button className="btn sm" disabled={!canWrite || busy} onClick={() => void refreshAll()}>
              ⟲ Refresh all
            </button>
          )}
        </div>

        {loading ? (
          <SkeletonRows n={4} cols={3} />
        ) : sources.length === 0 ? (
          <Empty
            title="No knowledge base sources yet"
            body="Add a URL or upload a document to ground replies in your own material."
            icon="▤"
          />
        ) : (
          sources.map((s) => {
            const st = status(s);
            return (
              <div className="card" key={s.id} style={{ marginBottom: "var(--sp-2)", padding: 0 }}>
                <div className="row" style={{ padding: "var(--sp-3)", alignItems: "flex-start" }}>
                  <Toggle checked={s.is_enabled} onChange={(v) => void toggle(s, v)} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="row">
                      <span className="strong truncate">{s.title || s.file_name || s.url}</span>
                      <Badge tone={st.tone}>{st.label}</Badge>
                    </div>
                    <div className="muted truncate" style={{ fontSize: 12 }}>
                      {s.source_type === "file" ? s.file_name : s.url}
                    </div>
                    {!!s.tags?.length && (
                      <div className="chips" style={{ marginTop: 4 }}>
                        {s.tags.slice(0, 5).map((t) => (
                          <span className="tag" key={t}>
                            {t}
                          </span>
                        ))}
                        {s.tags.length > 5 && <span className="muted">+{s.tags.length - 5}</span>}
                      </div>
                    )}
                  </div>
                  <div className="row">
                    {s.source_type === "file" ? (
                      <button className="btn icon sm ghost" title="View file" onClick={() => void viewFile(s)}>
                        ◉
                      </button>
                    ) : (
                      s.url && (
                        <a
                          className="btn icon sm ghost"
                          title="Visit URL"
                          href={s.url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          ↗
                        </a>
                      )
                    )}
                    <button
                      className="btn icon sm ghost"
                      title="Details"
                      onClick={() => setExpanded(expanded === s.id ? null : s.id)}
                    >
                      {expanded === s.id ? "▴" : "▾"}
                    </button>
                    <button
                      className="btn icon sm ghost"
                      title={s.source_type === "file" ? "Re-extract from file" : "Re-fetch and extract"}
                      disabled={!canWrite}
                      onClick={() => void refresh(s.id)}
                    >
                      ⟲
                    </button>
                    <button
                      className="btn icon sm ghost"
                      title="Delete"
                      disabled={!canWrite}
                      onClick={() => setToDelete(s)}
                    >
                      ✕
                    </button>
                  </div>
                </div>

                {expanded === s.id && (
                  <div className="card-foot">
                    {s.summary && (
                      <p>
                        <span className="strong">Summary: </span>
                        {s.summary}
                      </p>
                    )}
                    {s.fetch_error && (
                      <p style={{ color: "var(--critical)" }}>
                        <span className="strong">Error: </span>
                        {s.fetch_error}
                      </p>
                    )}
                    {s.content && (
                      <>
                        <div className="strong">Extracted content</div>
                        <pre className="pre-block">{s.content}</pre>
                      </>
                    )}
                    <div className="row muted" style={{ fontSize: 12 }}>
                      {s.last_fetched_at && <span>Last fetched {fmtDate(s.last_fetched_at)}</span>}
                      {s.last_fetched_at && (
                        <button className="btn sm ghost" disabled={!canWrite} onClick={() => void reExtract(s.id)}>
                          Re-extract
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      <Confirm
        open={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={() => void remove()}
        title="Delete this source?"
        body={`"${toDelete?.title || toDelete?.file_name || toDelete?.url || ""}" and its extracted text will be removed from the knowledge base.`}
        danger
      />
    </div>
  );
}
